import { updateProfile, updateProfiles } from 'flow/actions/profile-actions';
import FilterSearchBar from 'flow/components/FilterSearchBar';
import Divider from 'flow/components/ui-components/Divider';
import useAppContext from 'flow/hooks/useAppContext';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { MainNumber, Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';

import { TypedMap } from 'flow/types/TypedMap';
import { listToMap } from 'flow/utils';
import {
    updateFixedProfile,
    updateMobileProfile,
} from 'flow/utils/profile-utils';
import { fuzzySearch } from 'flow/utils/search-utils';
import { generatePath } from 'flow/utils/router-utils';
import React, { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import Loading from 'flow/components/ui-components/Loading';
import SettingSection from '../Setting/SettingSection';
import SettingSelectableItem from '../ui-components/SettingSelectableItem';
import { List as Ilist } from 'immutable';

const CallerIdSettingsEdit = () => {
    const [profileCallerId, setProfileCallerId] = useState('');
    const [varies, setVaries] = useState(false);
    const [filterText, setFilterText] = useState('');
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const { extensionKey, numberType, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        numberType: 'fixed' | 'msisdn';
        profileKey?: ProfileKey;
    }>();

    const mainNumbers = useAppSelector((state) =>
        state.profiles.get('main_numbers'),
    );

    const { profiles, getProfile } = useProfiles(extensionKey);

    const settingsAreReady = !!numberType && !!extensionKey && !!profiles;

    const handleSave = (profileCallerId: string) => {
        const updateNumberProfile =
            numberType === 'fixed' ? updateFixedProfile : updateMobileProfile;

        if (profileKey) {
            const profile = getProfile(profileKey);
            if (profile) {
                dispatch(
                    updateProfile(
                        extensionKey,
                        updateNumberProfile(profile, profileCallerId),
                    ),
                );
            }
        } else {
            dispatch(
                updateProfiles(
                    extensionKey,
                    profiles.map((profile) =>
                        updateNumberProfile(profile, profileCallerId),
                    ),
                ),
            );
        }

        if (profileKey) {
            history.push(
                generatePath(routes['settings.callerid.profile.numberType'], {
                    extensionKey,
                    numberType,
                }),
            );
        } else {
            history.push(
                generatePath(routes['settings.callerid'], { extensionKey }),
            );
        }
    };

    const prepareSettingsState = (callerIdType: string, varies: boolean) => {
        setProfileCallerId(callerIdType);
        setVaries(varies);
        setFilterText('');
    };

    const prepareMainNumberState = (
        first: TypedMap<Profile>,
        profiles: Ilist<TypedMap<Profile>>,
        mainNumber: string,
    ) => {
        const mainNumberKey = first.getIn(['callerId', mainNumber, 'key']);
        const secondFilter = profiles.filter(
            (profile) =>
                profile.getIn(['callerId', mainNumber, 'key']) ===
                mainNumberKey,
        );

        /**
         * If the size of the filtered list matches the incoming profiles, the listed items have
         * matching keys and we want to present the matching number else "varies".
         */
        if (secondFilter.size === profiles.size) {
            prepareSettingsState(mainNumberKey, false);
        } else {
            prepareSettingsState('varies', true);
        }
    };

    const prepSettingsKey = (
        profiles: Ilist<TypedMap<Profile>>,
        callerIdType: string,
        mainNumber: string,
    ) => {
        const first = profiles.first();
        const refCallerIdType = first.getIn(['callerId', callerIdType]);
        const filtered = profiles.filter(
            (profile) =>
                profile.getIn(['callerId', callerIdType]) === refCallerIdType,
        );

        /**
         * If the size of the filtered list matches the incoming profiles, the listed items have
         * matching callerId and we want to present the matching string else "varies".
         */
        if (filtered.size === profiles.size) {
            switch (refCallerIdType) {
                case 'mainanumber':
                    prepareMainNumberState(first, profiles, mainNumber);
                    break;
                case 'fixed':
                case 'msisdn':
                case 'hidden':
                    prepareSettingsState(refCallerIdType, false);
            }
        } else {
            prepareSettingsState('varies', true);
        }
    };

    useEffect(() => {
        if (!settingsAreReady) {
            return;
        }
        const profile = profileKey
            ? profiles.filter((profile) => profile.get('key') === profileKey)
            : profiles;
        switch (numberType) {
            case 'fixed':
                prepSettingsKey(
                    profile,
                    'fixedCallerIdType',
                    'fixedMainNumber',
                );
                break;
            case 'msisdn':
                prepSettingsKey(profile, 'mexCallerIdType', 'mexMainNumber');
                break;
            default:
        }
    }, [settingsAreReady]);

    if (!settingsAreReady || !mainNumbers || !profiles.first()) {
        return <Loading />;
    }

    const optionList = profiles.first().getIn(['callerId', 'callerIdOptions']);

    const handleChange = (value: string) => {
        setProfileCallerId(value);
        handleSave(value);
    };
    const handleSearch = (
        event: React.ChangeEvent<HTMLInputElement>,
        value: string,
    ): void => {
        setFilterText(value);
    };

    const renderOptionList = (item: string) => {
        const canShowMobileCallerIdFromFixed = profiles
            .first()
            .getIn(['callerId', 'canShowMobileCallerIdFromFixed']);

        if (
            numberType === 'fixed' &&
            item === 'msisdn' &&
            !canShowMobileCallerIdFromFixed
        ) {
            return null;
        }

        const descriptions: { [key: string]: string } = {
            fixed: i18next.t('settings.landline_number'),
            msisdn: i18next.t('settings.mobile_number'),
            hidden: i18next.t('settings.hidden_number'),
        };

        const description = descriptions[item];

        return description ? (
            <SettingSelectableItem
                key={`callerid-option-key-${item}`}
                value={item}
                onClick={handleChange}
                selected={profileCallerId}
            >
                {description}
            </SettingSelectableItem>
        ) : null;
    };

    let selectableItems = mainNumbers;

    if (filterText) {
        const CalleridKeyMapper = (item: TypedMap<MainNumber>) =>
            item
                ? {
                      key: item.get('key'),
                      description: item.get('description'),
                  }
                : null;

        const mainNumbersMap = listToMap(
            mainNumbers,
            (item: TypedMap<MainNumber>) => item.get('key'),
        );
        const searchResults = fuzzySearch(
            mainNumbers,
            filterText,
            CalleridKeyMapper,
            ['description'],
        );
        selectableItems = Ilist(
            searchResults.map((fuzzyResultItem: any) =>
                mainNumbersMap.get(fuzzyResultItem.item.key),
            ),
        );
    }

    return (
        <div>
            <SettingSection>
                {i18next.t('settings.callerid.my_numbers')}
            </SettingSection>

            {optionList.map(renderOptionList)}

            <Divider />

            <SettingSection className="mb-3">
                {i18next.t('settings.callerid.pbx_numbers')}
            </SettingSection>

            <FilterSearchBar
                searchText={filterText}
                placeholder={i18next.t('input.filter')}
                handleFilter={handleSearch}
            />
            {selectableItems.map((item) => (
                <SettingSelectableItem
                    key={item.get('key')}
                    value={item.get('key')}
                    onClick={handleChange}
                    selected={profileCallerId}
                >
                    {item.get('description')}
                </SettingSelectableItem>
            ))}

            {selectableItems.size === 0 &&
            (filterText || optionList.length === 0) ? (
                <div className="flex items-center justify-center leading-8">
                    {i18next.t('General.Search.Empty.Title')}
                </div>
            ) : null}
        </div>
    );
};

export default CallerIdSettingsEdit;
